<template>
  <Select
    class="vehicles-list__select"
    :placeholder="placeholder"
    :value="selectedVehicleId"
    :options="formattedVehicles"
    :empty-text="emptyStateText"
    :is-disabled="isDisabled"
    :select-first-by-default="selectFirstVehicle"
    value-key="id"
    label-key="displayLabel"
    @beforeChange="onBeforeChange"
    @input="handleVehicleSelect"
  >
    <template #option="{ option }">
      <div v-if="option" class="vehicles-list-option">
        <p
          v-if="option.revisedData"
          class="vehicles-list-option__verified-text"
        >
          <VerifiedCheckIcon class="vehicles-list-option__verified-icon" /> Перевірені дані
        </p>
        <p :class="clsx('vehicles-list-option__info-text', { 'vehicles-list-option__info-text--verified': option.revisedData })">
          <strong>Марка та модель:</strong> {{ option.mark_name }} {{ option.model_name }}
        </p>
        <p :class="clsx('vehicles-list-option__info-text', { 'vehicles-list-option__info-text--verified': option.revisedData })">
          <strong>Державний номер ТЗ:</strong> {{ option.registration_number }}
        </p>
        <p :class="clsx('vehicles-list-option__info-text', { 'vehicles-list-option__info-text--verified': option.revisedData })">
          <strong>Номер кузова (VIN-код):</strong> {{ option.vin }}
        </p>
        <p :class="clsx('vehicles-list-option__info-text', { 'vehicles-list-option__info-text--verified': option.revisedData })">
          <strong>Рік випуску авто:</strong> {{ option.year }}
        </p>
          <p
            v-if="option && option.registration_place && option.registration_place.name_full_name_ua"
            :class="clsx('vehicles-list-option__info-text', { 'vehicles-list-option__info-text--verified': option.revisedData })"
          >
          <strong>Місто:</strong> {{ option.registration_place.name_full_name_ua }}
        </p>
      </div>
    </template>
  </Select>
</template>

<script>
import clsx from 'clsx';
import { mapGetters } from 'vuex';
import { Select } from '@/shared/ui/form';
import { VerifiedCheckIcon } from '@/shared/ui/icons';

export default {
  model: {
    prop: 'vehicle',
    event: 'input'
  },
  props: {
    placeholder: {
      type: String,
      default: 'Оберіть авто клієнта',
    },
    vehicle: {
      type: [String, Number, null],
      default: null,
    },
    vehicles: {
      type: Array,
      default: () => [],
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    selectFirstVehicle: {
      type: Boolean,
      default: true,
    },
    emptyStateText: {
      type: String,
      default: 'Нічого немає',
    },
  },
  data() {
    return {
      selectedVehicleId: null
    };
  },
  mounted() {
    this.initializeSelection();
  },
  components: { Select, VerifiedCheckIcon },
  computed: {
    ...mapGetters('vehicleModule', ['isVehicleDataVerified']),

    formattedVehicles() {
      return this.vehicles
        ?.filter(vehicle => vehicle?.mark_name && vehicle?.model_name && vehicle?.registration_number && vehicle?.vin && vehicle?.year)
        ?.map(vehicle => ({
          ...vehicle,
          displayLabel: `${vehicle?.mark_name} ${vehicle?.model_name} (${vehicle?.registration_number})`,
          revisedData: this.isVehicleDataVerified(vehicle),
        }));
    },
  },
  methods: {
    clsx,
    initializeSelection() {
      if (this.vehicle != null) {
        const vehicleById = this.formattedVehicles.find(v => v.id === this.vehicle);
        if (vehicleById) {
          this.handleVehicleSelect(this.vehicle);
          return;
        }

        if (typeof this.vehicle === 'object') {
          const vehicleByProps = this.formattedVehicles.find(v => this.matchVehicleByProps(v, this.vehicle));
          if (vehicleByProps) {
            this.handleVehicleSelect(vehicleByProps.id);
            return;
          }
        }
      }

      if (this.vehicles?.length === 1 && this.selectFirstVehicle) {
        this.handleVehicleSelect(this.vehicles[0]?.id);
      }
    },
    matchVehicleByProps(vehicle, searchVehicle) {
      const normalize = (value) => value?.toString().toLowerCase().trim() ?? '';

      const registrationNumberMatch = vehicle?.registration_number && searchVehicle?.licensePlate
        && normalize(vehicle?.registration_number) === normalize(searchVehicle?.licensePlate);

      const vinMatch = vehicle?.vin && searchVehicle?.vin
        && normalize(vehicle?.vin) === normalize(searchVehicle?.vin);

      const yearMatch = vehicle?.year && searchVehicle?.year
        && normalize(vehicle?.year) === normalize(searchVehicle?.year);

      const cityIdMatch = vehicle?.cityId === searchVehicle?.cityId
        || vehicle?.registration_place?.id === searchVehicle?.cityId;

      const zoneMatch = vehicle?.zone === searchVehicle?.zone
        || vehicle?.registration_place?.zone === searchVehicle?.zone;

      const primaryMatch = registrationNumberMatch || vinMatch;

      return primaryMatch && yearMatch && cityIdMatch && zoneMatch;
    },
    handleVehicleSelect(selectedId) {
      const selectedVehicle = this.formattedVehicles.find(vehicle => vehicle.id === selectedId);
      this.selectedVehicleId = selectedVehicle ? selectedId : null;
      this.$emit('input', selectedVehicle || null);
      this.$emit('change', selectedVehicle || null);
    },
    onBeforeChange(newValue, oldValue) {
      this.$emit('beforeChange', newValue, oldValue);
    },
  },
  watch: {
    vehicle: {
      handler(newVehicle) {
        if (newVehicle != null) {
          const vehicleById = this.formattedVehicles.find(v => v.id === newVehicle);
          if (vehicleById) {
            this.handleVehicleSelect(newVehicle);
            return;
          }

          if (typeof newVehicle === 'object') {
            const vehicleByProps = this.formattedVehicles.find(v => this.matchVehicleByProps(v, newVehicle));
            if (vehicleByProps) {
              this.handleVehicleSelect(vehicleByProps.id);
              return;
            }
          }
        } else {
          this.selectedVehicleId = null;
          this.$emit('input', null);
          this.$emit('change', null);
        }
      },
    },
    vehicles: {
      handler() {
        this.initializeSelection();
      },
    }
  },
};
</script>

<style lang="scss" scoped>
.vehicles-list__select {
  width: 100%;
}

.vehicles-list-option {
  position: relative;

  &__verified-icon {
    margin-right: 3px;
  }

  &__verified-text {
    position: absolute;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    font-size: 12px;
    top: 0;
    right: 0;

    @media (max-width: 600px) {
      position: static;
      margin-bottom: 3px;
    }
    margin-bottom: 3px;
  }

  &__info-text {
    margin: 0;

    &--verified {
      font-style: italic;
    }

    &:not(:last-child) {
      margin-bottom: 4px;
    }

    strong {
      font-weight: 600;
    }
  }
}

::v-deep .vehicles-list-option__verified-text {
  position: static;
}
</style>
